import * as yup from 'yup';

import { FormErrors } from '@blockpulse3/data/shared';
import { AssetType, SubscriptionWorkflowType } from '@blockpulse3/graphql/hooks';
import {
  requiredBooleanField,
  requiredNumberField,
  requiredStringField,
} from '@blockpulse3/web-client/forms';

import { defaultHoldingMethodOptions } from '../../AllowedHoldingMethodSelect';
import { ICrowdfundingFundraisingParametersForm } from './types';

export const crowdfundingSubscriptionPeriodOptions = [
  { label: '6 semaines', value: 42 },
  { label: '8 semaines', value: 56 },
  { label: '12 semaines', value: 84 },
];

export const schemaFundraisingCrowdfunding = yup.object({
  name: requiredStringField,
  subscriptionPeriod: yup.object({
    label: requiredStringField,
    value: requiredNumberField,
  }),
  valuation: requiredNumberField,
  pricePerShare: requiredNumberField,
  assetType: requiredStringField,
  hardCap: requiredNumberField.test(
    'hardCap-min',
    FormErrors.HardCapGreaterThanNominalValue,
    (value, context) => {
      if (!value || value === 0 || !context.parent.pricePerShare) {
        return true;
      }

      return context.parent.pricePerShare <= value;
    },
  ),
  hasMifid: requiredBooleanField,
  subscriptionWorkflowType: requiredStringField,
  softCap: requiredNumberField.test(
    'max-hardcap',
    FormErrors.SoftGreaterThanHardCap,
    (value, context) => {
      if (!value || value === 0 || !context.parent.pricePerShare) {
        return true;
      }

      return context.parent.hardCap > value;
    },
  ),
  minimalAmount: yup
    .number()
    .transform((value) => (isNaN(value) || value === null || value === undefined ? 0 : value))
    .nullable()
    .test('min-max', FormErrors.MinimalAmountGreaterThanMaximal, (value, context) => {
      if (!value || value === 0 || !context.parent.maximalAmount) {
        return true;
      }
      return context.parent.maximalAmount >= value;
    })
    .test('min-val', FormErrors.MinimalAmountGreaterThanNominalValue, (value, context) => {
      if (!value || value === 0 || !context.parent.pricePerShare) {
        return true;
      }
      return context.parent.pricePerShare <= value;
    })
    .test('min-hardCap', FormErrors.AmountGreaterThanHardCap, (value, context) => {
      if (!value || value === 0) {
        return true;
      }
      return value < context.parent.hardCap;
    })
    .test('min-negative', FormErrors.PositiveFormat, (value) => {
      if (!value || value === 0) {
        return true;
      }
      return value >= 0;
    }),
  maximalAmount: yup
    .number()
    .transform((value) => (isNaN(value) || value === null || value === undefined ? 0 : value))
    .nullable()
    .test('max-min', FormErrors.MaximalAmountLessThanMinimal, (value, context) => {
      if (!value || value === 0 || !context.parent.minimalAmount) {
        return true;
      }
      return value >= context.parent.minimalAmount;
    })
    .test('max-hardCap', FormErrors.AmountGreaterThanHardCap, (value, context) => {
      if (!value || value === 0) {
        return true;
      }
      return value < context.parent.hardCap;
    })
    .test('max-negative', FormErrors.PositiveFormat, (value) => {
      if (!value || value === 0) {
        return true;
      }
      return value >= 0;
    }),
}) as yup.ObjectSchema<ICrowdfundingFundraisingParametersForm>;

export const crowdfundingParametersDefaultValues: ICrowdfundingFundraisingParametersForm = {
  name: '',
  subscriptionPeriod: crowdfundingSubscriptionPeriodOptions[0],
  valuation: 0,
  pricePerShare: 0,
  assetType: AssetType.ORDINARY_SHARE,
  allowedHoldingMethods: defaultHoldingMethodOptions,
  hardCap: null,
  subscriptionWorkflowType: SubscriptionWorkflowType.FIRST_IN,
  minimalAmount: null,
  maximalAmount: null,
  hasMifid: false,
  softCap: null,
};
